import React from 'react'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useTranslation } from 'react-i18next'
import LoopIcon from '@mui/icons-material/Loop'
import { Button, IconButton, Tooltip } from '@mui/material'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'

export interface PlaylistItemControlsCardProps {
  icon: JSX.Element | null | undefined
  title: string
  iconBackground: 'light' | 'dark'
  subText?: string
  description: string
  actions: {
    nextItem: () => void
    previousItem: () => void
    handleLoop: () => void
  }
  isLoop: boolean
  disablePrev?: boolean
  disableNext?: boolean
}

const PlaylistItemControlsCard: React.FunctionComponent<
  PlaylistItemControlsCardProps
> = ({
  icon,
  title,
  iconBackground,
  subText,
  actions,
  description,
  isLoop,
  disableNext,
  disablePrev,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isDesktop = useShowOnDesktop()

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      gap={4}
      width="100%"
    >
      {/* Icon Box */}
      {isDesktop && (
        <Box
          width={{ xs: 108, md: 250 }}
          height={{ xs: 108, md: 250 }}
          mx="auto"
          display="flex"
          justifyContent="center"
          alignItems="center"
          color="white"
          sx={{
            background:
              iconBackground === 'dark'
                ? theme.palette.primary.main
                : theme.palette.customBackground.logo,
          }}
        >
          {icon}
        </Box>
      )}

      {/* Text and controls */}
      <Box
        display={'flex'}
        flexDirection={'column'}
        width={isDesktop ? '92%' : '100%'}
      >
        <Typography variant="h6" component="h3" mt={{ xs: 2, md: 0 }}>
          {t('PlaylistControllers.Card.Title', '{{title}}', { title })}
        </Typography>

        <Box mt={2} mb={3} color="primary.main">
          <Typography variant="body1" component="p">
            {description}
          </Typography>
        </Box>

        {subText && (
          <Typography
            mt={theme.spacing(1)}
            variant="body1"
            component="p"
            color={theme.palette.primary.dark}
          >
            {subText}
          </Typography>
        )}

        {/* Controls */}
        <Box
          flex={1}
          my={{ xs: theme.spacing(2), md: 0 }}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'center', md: 'flex-end' }}
          flexDirection={{ xs: 'row', md: 'row' }}
        >
          <Tooltip title="Loop All">
            <IconButton onClick={actions.handleLoop}>
              <LoopIcon
                sx={{
                  boxShadow: isLoop
                    ? '0 0 6px 2px rgba(0, 0, 0, 0.12)'
                    : 'none',
                  borderRadius: '50%',
                  transition: 'box-shadow 0.3s ease, color 0.3s ease',
                  display: 'inline-block',
                }}
                fontSize="large"
                color={isLoop ? 'success' : 'primary'}
              />
            </IconButton>
          </Tooltip>

          <Box
            display="flex"
            flexDirection={{ xs: 'row', md: 'column' }}
            gap={theme.spacing(1)}
            width={{ md: 250 }}
          >
            <Button
              variant="outlined"
              color="success"
              onClick={actions.previousItem}
              disabled={disablePrev}
            >
              {t('PlaylistItemControlsCard.Button.Previous', 'Previous')}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={actions.nextItem}
              disabled={disableNext}
            >
              {t('PlaylistItemControlsCard.Button.Next', 'Next')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PlaylistItemControlsCard
